import React, { useMemo, useState } from 'react'
import Button from '../../../components/Form/Button'
import Input from '../../../components/Form/Input'
import { CustomContentModal } from '../../../components/CustomContentModal'
import ModifiedDescriptionField from '../programOnboarding/ModifiedDescriptionField'
import { updateVNEMPercentagesApi } from '../../../apis/apis'
import type { VNEMAllocationSubmission } from '../../../apis/types'
import { formatUTCDate } from '../../../util/string'
import classNames from 'classnames'
import {
  ALLOCATION_SUM_EPSILON,
  allocationInEffectOnDateOnly,
  sumInEffectOverlayPercentages,
  VNEM_FUTURE_END_DATE_ONLY,
} from '../vnemAllocationMath'

export { ALLOCATION_SUM_EPSILON }

export const VNEM_PERCENTAGE_EDIT_WARNING =
  "Editing VNEM allocation percentages on an active VNEM table may cause King's billing calculations to diverge from the utility's if the allocation percentages don't match what is active at the utility exactly."

interface EditVNEMPercentagesModalProps {
  isOpen: boolean
  programId: string
  submission: VNEMAllocationSubmission
  onClose: () => void
  onSaved: () => void
}

interface DraftRow {
  meterCuid: string
  meterNumber: string
  serviceAddress: string
  currentPercentage: number
  newPercentage: string
}

type Step = 'form' | 'confirm'

interface ApiErrorShape {
  response?: { data?: { message?: string } }
  message?: string
}

const toDateOnly = (value: string | Date | null | undefined): string | null => {
  if (value == null || value === '') return null
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString().slice(0, 10)
}

const todayUtc = (): string => new Date().toISOString().slice(0, 10)

const parsePercentage = (value: string): number | null => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return null
  return parsed
}

const EditVNEMPercentagesModal: React.FC<EditVNEMPercentagesModalProps> = ({
  isOpen,
  programId,
  submission,
  onClose,
  onSaved,
}) => {
  const minDate = toDateOnly(submission.utilityEffectiveDate) ?? toDateOnly(submission.startDate) ?? todayUtc()
  const maxDate =
    toDateOnly(submission.utilityEndDate) ?? toDateOnly(submission.endDate) ?? VNEM_FUTURE_END_DATE_ONLY
  const defaultEffective = todayUtc() < minDate ? minDate : todayUtc() > maxDate ? maxDate : todayUtc()

  const [rows, setRows] = useState<DraftRow[]>(() => {
    const missing = submission.vnemAllocations.find((alloc) => alloc.meterCuid == null || alloc.meterCuid === '')
    if (missing != null) {
      throw new Error('VNEM allocation items must include meterCuid')
    }
    return submission.vnemAllocations.map((alloc) => ({
      meterCuid: alloc.meterCuid,
      meterNumber: alloc.meterNumber,
      serviceAddress: alloc.serviceAddress,
      currentPercentage: Number(alloc.percentage),
      newPercentage: Number(alloc.percentage).toFixed(2),
    }))
  })
  const [effectiveDate, setEffectiveDate] = useState(defaultEffective)
  const [modifiedDescription, setModifiedDescription] = useState('')
  const [step, setStep] = useState<Step>('form')
  const [isSaving, setIsSaving] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const [reasonError, setReasonError] = useState<string | null>(null)

  const changes = useMemo(
    () =>
      rows.filter((row) => {
        const next = parsePercentage(row.newPercentage)
        return next != null && Math.abs(next - row.currentPercentage) > 0.0001
      }),
    [rows]
  )

  const percentageOverrides = useMemo(() => {
    const overrides = new Map<string, number>()
    for (const row of rows) {
      const next = parsePercentage(row.newPercentage)
      if (next != null) {
        overrides.set(row.meterCuid, next)
      }
    }
    return overrides
  }, [rows])

  const total = useMemo(
    () =>
      sumInEffectOverlayPercentages(
        submission.vnemAllocations.map((alloc) => ({
          meterCuid: alloc.meterCuid,
          percentage: Number(alloc.percentage),
          startDate: alloc.startDate,
          endDate: alloc.endDate,
        })),
        effectiveDate,
        percentageOverrides
      ),
    [submission.vnemAllocations, effectiveDate, percentageOverrides]
  )
  const totalIsValid = Math.abs(total - 100) <= ALLOCATION_SUM_EPSILON

  const changesInEffect = useMemo(
    () =>
      changes.every((row) => {
        const alloc = submission.vnemAllocations.find((item) => item.meterCuid === row.meterCuid)
        return (
          alloc != null && allocationInEffectOnDateOnly(alloc.startDate, alloc.endDate, effectiveDate)
        )
      }),
    [changes, submission.vnemAllocations, effectiveDate]
  )

  const canSave =
    totalIsValid &&
    changes.length > 0 &&
    changesInEffect &&
    modifiedDescription.trim() !== '' &&
    effectiveDate !== '' &&
    !isSaving

  const handlePercentageChange = (meterCuid: string, value: string): void => {
    setRows((current) => current.map((row) => (row.meterCuid === meterCuid ? { ...row, newPercentage: value } : row)))
  }

  const submit = async (): Promise<void> => {
    setIsSaving(true)
    setApiError(null)
    try {
      await updateVNEMPercentagesApi(programId, submission.cuid, {
        effectiveDate,
        modifiedDescription: modifiedDescription.trim(),
        allocations: changes.map((row) => ({
          meterCuid: row.meterCuid,
          percentage: parsePercentage(row.newPercentage) as number,
        })),
      })
      onSaved()
    } catch (e: unknown) {
      const error = e as ApiErrorShape
      setApiError(error.response?.data?.message ?? error.message ?? 'Something went wrong. Please try again.')
      setStep('form')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveClick = (): void => {
    if (modifiedDescription.trim() === '') {
      setReasonError('Required')
      return
    }
    setReasonError(null)
    setApiError(null)
    setStep('confirm')
  }

  return (
    <CustomContentModal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-3xl w-full p-6">
        {step === 'form' && (
          <>
            <h3 className="text-lg font-semibold mb-1 text-slate-900 dark:text-slate-100">Edit allocations</h3>
            <div
              className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-950 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-100"
              role="alert"
            >
              {VNEM_PERCENTAGE_EDIT_WARNING}
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              Only the active-at-utility submission can be edited. Saving closes out the current percentage and opens a
              new dated row as of the effective date.
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    <th className="py-2 pr-4">Meter</th>
                    <th className="py-2 pr-4">Service address</th>
                    <th className="py-2 pr-4">Current %</th>
                    <th className="py-2">New %</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const next = parsePercentage(row.newPercentage)
                    const changed = next != null && Math.abs(next - row.currentPercentage) > 0.0001
                    return (
                      <tr
                        key={row.meterCuid}
                        className={classNames(
                          'border-t border-slate-200 dark:border-slate-700',
                          changed && 'bg-amber-50 dark:bg-amber-950/40'
                        )}
                      >
                        <td className="py-2 pr-4 font-medium text-slate-900 dark:text-slate-100">{row.meterNumber}</td>
                        <td className="py-2 pr-4 text-slate-600 dark:text-slate-300">{row.serviceAddress}</td>
                        <td className="py-2 pr-4 text-slate-700 dark:text-slate-200">{row.currentPercentage.toFixed(2)}</td>
                        <td className="py-2">
                          <Input
                            aria-label={`New percentage for ${row.meterNumber}`}
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={row.newPercentage}
                            onChange={(e) => handlePercentageChange(row.meterCuid, e.target.value)}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div
              className={classNames(
                'mb-4 text-sm font-medium',
                totalIsValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}
            >
              Total: {total.toFixed(2)}% {totalIsValid ? '' : '(must be 100 ± 0.01 to save)'}
            </div>

            <div className="mb-4">
              <Input
                label="Effective date"
                required
                type="date"
                value={effectiveDate}
                min={minDate}
                max={maxDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </div>

            <ModifiedDescriptionField
              value={modifiedDescription}
              onChange={(value) => {
                setModifiedDescription(value)
                if (value.trim() !== '') setReasonError(null)
              }}
              error={reasonError ?? undefined}
              className="mb-4"
            />

            {apiError != null && <p className="mb-4 text-sm text-red-600 dark:text-red-500">{apiError}</p>}

            <div className="flex gap-3 justify-end">
              <Button color="slate" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSaveClick} disabled={!canSave}>
                Save
              </Button>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <>
            <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-100">Confirm allocation changes</h3>
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-4">{VNEM_PERCENTAGE_EDIT_WARNING}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              Effective {formatUTCDate(effectiveDate)}. These percentages will close out the current rows and create new
              ones as of that date.
            </p>
            <ul className="mb-6 space-y-2 text-sm">
              {changes.map((row) => (
                <li key={row.meterCuid} className="text-slate-800 dark:text-slate-200">
                  {row.meterNumber}: {row.currentPercentage.toFixed(2)}% → {parsePercentage(row.newPercentage)?.toFixed(2)}%
                </li>
              ))}
            </ul>
            <div className="flex gap-3 justify-end">
              <Button color="slate" onClick={() => setStep('form')} disabled={isSaving}>
                Back
              </Button>
              <Button onClick={() => void submit()} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Confirm'}
              </Button>
            </div>
          </>
        )}
      </div>
    </CustomContentModal>
  )
}

export default EditVNEMPercentagesModal
